// components/Chatbot/ChatDisabledState.tsx
import { Sparkles } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';

export interface PendingHelpRequest {
  action?: string;
  message?: string;
  [key: string]: unknown;
}

export interface ChatDisabledStateProps {
  pendingHelpRequest?: PendingHelpRequest | null;
  onClearPendingRequest?: () => void;
}

const getContextMessage = (pendingRequest: PendingHelpRequest | null | undefined): string | null => {
  if (!pendingRequest) return null;

  const { action } = pendingRequest;

  if (action === 'setup_major') {
    return 'Enable to get help setting up your major';
  } else if (action === 'setup_semesters') {
    return 'Enable to get help setting up your semesters';
  } else if (action === 'requirement_help') {
    return 'Enable to get help with this requirement';
  } else if (action === 'suggest_courses') {
    return 'Enable to generate course suggestions';
  } else if (action === 'clear_suggestions') {
    return 'Enable to clear course suggestions';
  }

  return 'Enable to get help from the AI Assistant';
};

const ChatDisabledState = ({
  pendingHelpRequest,
  onClearPendingRequest,
}: ChatDisabledStateProps) => {
  const { setEnabled, isLoading } = useChatbot();

  const handleEnable = async (): Promise<void> => {
    try {
      await setEnabled(true);
    } catch (error) {
      console.error('Failed to enable chatbot:', error);
    }
  };

  const handleDismiss = (): void => {
    if (onClearPendingRequest) {
      onClearPendingRequest();
    }
  };

  const contextMessage = getContextMessage(pendingHelpRequest);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Sparkles className="h-12 w-12 text-purple-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">AI Assistant Disabled</h3>
        {contextMessage && <p className="text-sm text-gray-600 mb-4">{contextMessage}</p>}
        <div className="flex items-center gap-2">
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 text-sm text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg px-4 py-2 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            <span>{isLoading ? 'Enabling...' : 'Enable AI Assistant'}</span>
          </button>
          {pendingHelpRequest && (
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 transition-colors"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatDisabledState;
